/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import Spinner from '../../../atoms/Spinner/Spinner';
import { PendingUser } from '../../../../types/users/PendingUser';
import ExpandableCard from '../../../atoms/Card/ExpandableCard';
import CardBody from '../../../atoms/Card/CardBody';
import useTableExpanded from '../../../../services/hooks/useTableExpanded';
import ErrorMessage from '../../../atoms/Message/Error/ErrorMessage';
import { ErrorType } from '../../../../types/response/ErrorCodes';
import Table from '../../../atoms/Table/Table';
import { ColumnDef } from '../../../atoms/Table/tableFeatures';
import DecoratorInput from '../../../atoms/Input/DecoratorInput';
import FilterLineIcon from 'remixicon-react/Filter3LineIcon';
import useFilter from '../../../../services/hooks/useFilter';
import TwoRowCell from '../../../atoms/Table/TwoRowCell';
import { formatDate } from '../../../../services/helpers/dateTimeFormats';
import { usePendingUsersStore } from '../../../../store/admin/pendingUsers/pendingUsersStore';
import MultiactionButton from '../../../atoms/Button/MultiactionButton';
import { deletePendingUser, inviteUser } from '../../../../api/pendingUsers/pendingUsers';
import DeleteBinLineIcon from 'remixicon-react/DeleteBinLineIcon';
import More2LineIcon from 'remixicon-react/More2LineIcon';
import SendPlaneLineIcon from 'remixicon-react/SendPlaneLineIcon';
import FileCopyLineIcon from 'remixicon-react/FileCopyLineIcon';
import InformationMessage from '../../../atoms/Message/Information/InformationMessage';
import { transparentize } from 'polished';
import useLogTimeSpentOnPage from '../../../../services/hooks/useLogTimeSpentOnPage';
import { config } from '../../../../config';
import Logger from '../../../../services/logging/logger';

interface SearchResultsProps {
    hasWrite: boolean;
    data: PendingUser[];
    theme: DefaultTheme;
    organizationId: string;
}

const PendingUserSearchResults: React.FC<SearchResultsProps> = ({
    hasWrite,
    data,
    theme,
    organizationId,
}: SearchResultsProps) => {
    const { isTableExpanded, toggleTableExpanded } = useTableExpanded();
    const {
        isLoading,
        searchError,
        inviteError,
        deleteError,
        inviteStarted,
        inviteFailed,
        deleteStarted,
        deleteFailed,
        searchStarted,
        searchSucceeded,
        searchFailed,
    } = usePendingUsersStore();

    useLogTimeSpentOnPage('pendingusers');
    const now = new Date().getTime();

    const filterUsers = (x: PendingUser, filterBySearchWord: (property: string) => boolean) =>
        filterBySearchWord(x.name) ||
        filterBySearchWord(x.email) ||
        x.groups.some(group => filterBySearchWord(group));

    const { search, setSearch, showing, showFilter } = useFilter(data, filterUsers);

    const PendingUserRowBackground = (expiresAt: string) => {
        if (now < new Date(expiresAt).getTime()) return 'transparent';

        return transparentize(0.9, theme.colors.pendingUsers.status.expired);
    };

    const copyInviteLink = (pendingUserId: string) => {
        navigator.clipboard.writeText(`${config.PublicUrl}/invitation/${pendingUserId}`);
        Logger.information('Copied pending user invite link', {
            OrganizationId: organizationId,
            PendingUserId: pendingUserId,
        });
    };

    const columns: ColumnDef<PendingUser>[] = [
        {
            header: 'Name',
            accessorKey: 'name',
            cell: (props: { row: { original: PendingUser } }) => {
                return <div>{props.row.original.name}</div>;
            },
        },
        {
            header: 'Email',
            accessorKey: 'email',
            cell: (props: { row: { original: PendingUser } }) => {
                return <div>{props.row.original.email}</div>;
            },
        },
        {
            header: 'Groups',
            accessorKey: 'groups',
            cell: (props: { row: { original: PendingUser } }) => {
                const { groups } = props.row.original;
                return <div>{groups.join(', ')}</div>;
            },
        },
        {
            header: 'Sent',
            accessorKey: 'timestampSent',
            enableSorting: false,
            cell: (props: { row: { original: PendingUser } }) => {
                const { sentAt } = props.row.original;

                const timeArray = formatDate(new Date(sentAt));
                return <TwoRowCell upper={timeArray.yyyyMMdd} lower={timeArray.HHmm} />;
            },
        },
        {
            header: 'Expires',
            accessorKey: 'timestampExpires',
            enableSorting: false,
            cell: (props: { row: { original: PendingUser } }) => {
                const { validUntil } = props.row.original;

                const timeArray = formatDate(new Date(validUntil));
                return <TwoRowCell upper={timeArray.yyyyMMdd} lower={timeArray.HHmm} />;
            },
        },
        {
            header: '',
            accessorKey: 'actions',
            enableSorting: false,
            cell: (props: { row: { original: PendingUser } }) => {
                return (
                    hasWrite && (
                        <MultiactionButton
                            actions={[
                                {
                                    key: 'pendingUser-copy-invite-link',
                                    dataTestId: `pendingUser-copy-invite-link-${props.row.original.id}`,
                                    label: (
                                        <>
                                            <FileCopyLineIcon size={theme.icon.size.small} />
                                            Copy invite link
                                        </>
                                    ),
                                    action: () => copyInviteLink(props.row.original.id),
                                    show: hasWrite,
                                },
                                {
                                    key: 'pendingUser-resend',
                                    dataTestId: `pendingUser-resend-${props.row.original.id}`,
                                    label: (
                                        <>
                                            <SendPlaneLineIcon size={theme.icon.size.small} />
                                            Resend
                                        </>
                                    ),

                                    action: () => {
                                        inviteUser(
                                            organizationId,
                                            props.row.original.id,
                                            inviteStarted,
                                            inviteFailed,
                                            searchStarted,
                                            searchSucceeded,
                                            searchFailed
                                        );
                                    },
                                    show: hasWrite,
                                },
                                {
                                    key: 'pendingUser-delete',
                                    dataTestId: `pendingUser-delete-${props.row.original.id}`,
                                    label: (
                                        <>
                                            <DeleteBinLineIcon size={theme.icon.size.small} />{' '}
                                            Delete
                                        </>
                                    ),
                                    action: () => {
                                        deletePendingUser(
                                            organizationId,
                                            props.row.original.id,
                                            deleteStarted,
                                            deleteFailed,
                                            searchStarted,
                                            searchSucceeded,
                                            searchFailed
                                        );
                                    },
                                    show: hasWrite,
                                },
                            ]}
                            dataTestId={`pendingUser-actions-${props.row.original.id}`}
                            noneButton
                        >
                            <More2LineIcon size={theme.icon.size.normal} />
                        </MultiactionButton>
                    )
                );
            },
        },
    ];

    const renderSearchResults = () => {
        if (searchError)
            return <ErrorMessage error={searchError} errorHeader={ErrorType.LoadPendingUsers} />;
        if (inviteError) return <ErrorMessage error={inviteError} errorHeader={ErrorType.Invite} />;
        if (deleteError)
            return <ErrorMessage error={deleteError} errorHeader={ErrorType.DeletePendingUser} />;

        if (data.length === 0) {
            return (
                <MessageWrapper>
                    <InformationMessage
                        messages={['No pending users found.']}
                        messageHeader="Empty list"
                    />
                </MessageWrapper>
            );
        }

        return (
            <>
                {showFilter && (
                    <SearchWrapper>
                        <DecoratorInput
                            type="text"
                            name="filterUsers"
                            onChange={e => setSearch(e.target.value)}
                            value={search}
                            placeholder="Filter users"
                            id="filter-users"
                            decorator={<FilterLineIcon size="17" />}
                        />
                    </SearchWrapper>
                )}

                <ExpandableCard expanded={isTableExpanded}>
                    <CardBody>
                        <Table<PendingUser>
                            data={showing}
                            columns={columns}
                            getRowStyles={row => ({
                                background: PendingUserRowBackground(row.validUntil),
                            })}
                            isExpanded={isTableExpanded}
                            onExpandToggle={toggleTableExpanded}
                        />
                    </CardBody>
                </ExpandableCard>
            </>
        );
    };

    return isLoading ? <Spinner loading={isLoading} /> : renderSearchResults();
};

export default withTheme(PendingUserSearchResults) as React.ComponentType<
    Omit<SearchResultsProps, 'theme'>
>;

const SearchWrapper = styled.div`
    margin-bottom: 1rem;
    background: ${props => props.theme.colors.light};
`;

const MessageWrapper = styled.div`
    margin: 2rem;
`;
